import { useState, useEffect, useRef } from 'react'
import { Button, Text, Group } from '@mantine/core'
import { IconPhoto, IconUpload, IconX, IconCamera, IconPhotoPlus } from '@tabler/icons-react'

interface ImageUploadProps {
    value: File | null
    onChange: (file: File | null) => void
    label?: string
    maxSizeText?: string
}

export function ImageUpload({
    value,
    onChange,
    label = "Image (optional)",
    maxSizeText = "PNG, JPG, GIF up to 10MB"
}: ImageUploadProps) {
    const [dragActive, setDragActive] = useState(false)
    const [preview, setPreview] = useState<string | null>(null)
    const [isMobile, setIsMobile] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const cameraInputRef = useRef<HTMLInputElement>(null)

    // Detect mobile device
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
        }

        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    // Update preview when value changes
    useEffect(() => {
        if (value) {
            const url = URL.createObjectURL(value)
            setPreview(url)
            return () => URL.revokeObjectURL(url)
        } else {
            setPreview(null)
        }
    }, [value])

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true)
        } else if (e.type === 'dragleave') {
            setDragActive(false)
        }
    }

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)

        const files = e.dataTransfer.files
        if (files && files[0] && files[0].type.startsWith('image/')) {
            onChange(files[0])
        }
    }

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file && file.type.startsWith('image/')) {
            onChange(file)
        }
        // Reset the input value to allow selecting the same file again
        e.target.value = ''
    }

    const openCameraDialog = () => {
        cameraInputRef.current?.click()
    }

    const openFileDialog = () => {
        fileInputRef.current?.click()
    }

    const removeImage = () => {
        onChange(null)
    }

    return (
        <div>
            <Text size="sm" fw={500} mb="xs">{label}</Text>

            {/* Camera input for mobile */}
            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                capture="environment"
            />

            {/* File input for gallery/desktop */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {preview ? (
                // Preview mode
                <div className="relative">
                    <img
                        src={preview}
                        alt="Preview"
                        className="w-full max-w-xs h-32 object-cover rounded-md border border-gray-200 dark:border-gray-600"
                    />
                    <Button
                        variant="subtle"
                        color="red"
                        size="xs"
                        className="absolute top-2 right-2"
                        onClick={removeImage}
                    >
                        <IconX size={14} />
                    </Button>
                    <Text size="xs" c="dimmed" mt="xs">
                        {value?.name}
                    </Text>
                </div>
            ) : (
                // Upload area
                <div>
                    {isMobile ? (
                        // Mobile: Two buttons for camera and photo library
                        <Group gap="sm">
                            <Button
                                variant="filled"
                                color="red"
                                size="lg"
                                flex={1}
                                leftSection={<IconCamera size={20} />}
                                onClick={openCameraDialog}
                                style={{
                                    touchAction: 'manipulation',
                                    height: '50px'
                                }}
                            >
                                Take Photo
                            </Button>
                            <Button
                                variant="outline"
                                color="blue"
                                size="lg"
                                flex={1}
                                leftSection={<IconPhotoPlus size={20} />}
                                onClick={openFileDialog}
                                style={{
                                    touchAction: 'manipulation',
                                    height: '50px'
                                }}
                            >
                                From Library
                            </Button>
                        </Group>
                    ) : (
                        // Desktop: Drag and drop area
                        <div
                            className={`
                                border-2 border-dashed rounded-md p-6 text-center transition-colors
                                ${dragActive
                                    ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                                }
                            `}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                        >
                            <div className="flex flex-col items-center gap-2">
                                <IconPhoto size={32} className="text-gray-400" />
                                <div>
                                    <Text size="sm" fw={500}>
                                        Drop an image here, or click below to select
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                        {maxSizeText}
                                    </Text>
                                </div>
                                <Button
                                    variant="light"
                                    size="sm"
                                    leftSection={<IconUpload size={16} />}
                                    onClick={openFileDialog}
                                    style={{ touchAction: 'manipulation' }}
                                >
                                    Choose File
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
} 